import {
  ClipboardCheck,
  History,
  Info,
  ReceiptText,
  Signature,
} from "lucide-react";
import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  Skeleton,
  useSidebar,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { cn } from "@/lib/utils/cn";
import {
  ExpenseIcon,
  FileIcon,
  IncidentIcon,
  LienIcon,
  LitigationIcon,
  MatterEmailIcon,
  MatterOverviewIcon,
  MedicalTreatmentIcon,
  NoteIcon,
  PropertyDamageIcon,
  SettlementIcon,
  TaskIcon,
} from "@/lib/utils/iconUtils";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

export function MatterDetailSubMenu({
  matterId,
  currentPath,
}: {
  matterId: string;
  currentPath: string;
}) {
  const { currentUser, hasPagePermission } = useUser();
  const { setOpenMobile } = useSidebar();
  const { data, isLoading, isError } = useQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = Array.isArray(data?.data) ? undefined : data?.data;
  const isDeletedMatter = !isLoading && !isError && !matter?.id;
  const basePath = `/matters/${matterId}`;
  const isOverviewActive =
    currentPath === basePath || currentPath === `${basePath}/`;
  const hasIncidentDetails =
    !matter?.incidentType ||
    matter.incidentType.incidentModuleAccess ||
    matter.incidentType.incidentReportModuleAccess;
  const hasCustomFields = matter?.massTortCustomFields.some((field) =>
    Boolean(field.massTortId),
  );
  const items = [
    {
      title: "Parties",
      path: "/matter-details/client",
      activePath: "/matter-details",
      icon: Info,
      visible: hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailParties),
    },
    {
      title: "Incident details",
      path: "/incident-details",
      icon: IncidentIcon,
      visible:
        hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailIncidentDetails) &&
        hasIncidentDetails,
    },
    {
      title: "Custom fields",
      path: "/custom-fields",
      icon: ClipboardCheck,
      visible:
        hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailCustomFields) &&
        hasCustomFields,
    },
    {
      title: "Property damage",
      path: "/property-damage",
      icon: PropertyDamageIcon,
      visible: hasPagePermission(
        PAGE_PERMISSION_KEYS.mattersDetailPropertyDamage,
      ),
    },
    {
      title: "Emails",
      path: "/emails",
      icon: MatterEmailIcon,
      visible: hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailEmails),
    },
    {
      title: "Liens",
      path: "/liens",
      icon: LienIcon,
      visible: hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailLiens),
    },
    {
      title: "Costs",
      path: "/costs",
      icon: ExpenseIcon,
      visible: hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailCosts),
    },
    {
      title: "Charges",
      path: "/charges",
      icon: ReceiptText,
      visible: currentUser.owner,
    },
    {
      title: "Medical treatments",
      path: "/medical-treatments",
      icon: MedicalTreatmentIcon,
      visible: hasPagePermission(
        PAGE_PERMISSION_KEYS.mattersDetailMedicalTreatments,
      ),
    },
    {
      title: "Settlements",
      path: "/settlements",
      icon: SettlementIcon,
      visible: hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailSettlements),
    },
    {
      title: "Litigation",
      path: "/litigation",
      icon: LitigationIcon,
      visible: hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailLitigation),
    },
    {
      title: "Tasks",
      path: "/tasks",
      icon: TaskIcon,
      visible: hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailTasks),
    },
    {
      title: "Notes",
      path: "/notes",
      icon: NoteIcon,
      visible: hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailNotes),
    },
    {
      title: "Updates",
      path: "/updates",
      icon: History,
      visible: hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailUpdates),
    },
    {
      title: "Files",
      path: "/files",
      activePath: "/files",
      icon: FileIcon,
      visible: hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailFiles),
    },
    {
      title: "Signature requests",
      path: "/signature-requests",
      activePath: "/signature-requests",
      icon: Signature,
      visible: hasPagePermission(
        PAGE_PERMISSION_KEYS.mattersDetailSignatureRequests,
      ),
    },
  ];

  return (
    <>
      {hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailOverview) ? (
        <SidebarMenuSubItem>
          {isDeletedMatter ? (
            <SidebarMenuSubButton aria-disabled className="min-h-10 text-lg">
              <MatterOverviewIcon className="mr-2 h-4 w-4 shrink-0" />
              <span className="min-w-0 truncate">Deleted matter</span>
            </SidebarMenuSubButton>
          ) : (
            <SidebarMenuSubButton asChild>
              <Link
                to={basePath}
                className={cn(
                  isOverviewActive &&
                    "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
                )}
                onClick={() => {
                  setOpenMobile(false);
                }}
              >
                {isLoading ? (
                  <div className="h-10 w-full py-1">
                    <Skeleton className="h-8 w-full rounded-md bg-neutral-900" />
                  </div>
                ) : (
                  <>
                    <MatterOverviewIcon className="mr-2 h-4 w-4 shrink-0" />
                    <span className="min-w-0 truncate" title={matter?.title}>
                      {isError
                        ? "Error loading matter"
                        : matter?.title || "Matter"}
                    </span>
                  </>
                )}
              </Link>
            </SidebarMenuSubButton>
          )}
        </SidebarMenuSubItem>
      ) : (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton aria-disabled>
            <MatterOverviewIcon className="mr-2 h-4 w-4 shrink-0" />
            <span className="min-w-0 truncate" title={matter?.title}>
              {isLoading
                ? "Loading matter..."
                : isError
                  ? "Error loading matter"
                  : matter?.title || "Matter"}
            </span>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      )}
      {!isDeletedMatter ? (
        <SidebarMenuSubItem className="list-none p-0">
          <SidebarMenuSub className="mt-0.5 mr-0 ml-0 border-l-2 border-l-neutral-300 py-0.5 pr-0 pl-3">
            {items
              .filter((item) => item.visible)
              .map((item) => {
                const activePath = `${basePath}${item.activePath ?? item.path}`;
                const isActive =
                  currentPath === activePath ||
                  (Boolean(item.activePath) &&
                    currentPath.startsWith(`${activePath}/`));
                return (
                  <SidebarMenuSubItem key={item.path}>
                    <SidebarMenuSubButton asChild>
                      <Link
                        to={`${basePath}${item.path}`}
                        className={cn(
                          isActive &&
                            "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
                        )}
                        onClick={() => {
                          setOpenMobile(false);
                        }}
                      >
                        {isLoading ? (
                          <div className="h-10 w-full py-1">
                            <Skeleton
                              className={cn(
                                "h-8 w-full rounded-md",
                                isActive ? "bg-neutral-900" : "bg-gray-500",
                              )}
                            />
                          </div>
                        ) : (
                          <>
                            <item.icon className="mr-2 h-4 w-4 shrink-0" />
                            <span className="min-w-0 truncate">
                              {item.title}
                            </span>
                          </>
                        )}
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
          </SidebarMenuSub>
        </SidebarMenuSubItem>
      ) : null}
    </>
  );
}
